import React, { useMemo, useState } from "react";
import { FaPlay } from "react-icons/fa";
import SamplesModal from "./SamplesModal";
import { formatSar, isPriceField } from "./formatPrice";
import { cardCopy, samplesCountLabel } from "./cardCopy";

const CHIP_TYPES = ["radio", "select", "multiselect", "country"];
const META_KEYS = ["country", "city", "age"];

const asList = (field, lng) => {
  const value = field.value;
  if (value === null || value === undefined || value === "") return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && item.label) return item.label[lng] || item.label.ar || item.value;
        return item == null ? "" : String(item);
      })
      .filter(Boolean);
  }

  if (typeof value === "object" && value.label) {
    return [value.label[lng] || value.label.ar || value.value].filter(Boolean);
  }

  return [String(value)];
};

const TalentCard = ({ talent, lng }) => {
  const [open, setOpen] = useState(false);
  const copy = cardCopy(lng);

  const groups = useMemo(() => {
    const chips = [];
    const prices = [];
    const bio = [];
    const rest = [];
    const meta = [];

    (talent.fields || []).forEach((field) => {
      if (isPriceField(field)) {
        const amount = formatSar(field.value, lng);
        if (amount) prices.push({ key: field.key, label: field.label[lng], amount });
        return;
      }

      if (field.type === "textarea") {
        const text = asList(field, lng).join(" ");
        if (text) bio.push({ key: field.key, label: field.label[lng], text });
        return;
      }

      if (META_KEYS.includes(field.key)) {
        const text = asList(field, lng).join(" ");
        if (text) {
          meta.push(field.key === "age" ? `${text} ${lng === "en" ? "yrs" : "سنة"}` : text);
        }
        return;
      }

      if (CHIP_TYPES.includes(field.type)) {
        const items = asList(field, lng);
        if (items.length) chips.push({ key: field.key, label: field.label[lng], items });
        return;
      }

      const text = asList(field, lng).join("، ");
      if (text) rest.push({ key: field.key, label: field.label[lng], text });
    });

    return { chips, prices, bio, rest, meta };
  }, [talent.fields, lng]);

  const sampleCount = (talent.samples || []).filter((sample) => sample.audio_url).length;
  const previewTypes = (talent.samples || [])
    .map((sample) =>
      sample.performance_type && sample.performance_type.label
        ? sample.performance_type.label[lng]
        : null
    )
    .filter(Boolean)
    .filter((label, index, all) => all.indexOf(label) === index)
    .slice(0, 4);

  return (
    <>
      <article className="talent-card w-full bg-white rounded-2xl border border-purpleLight/50 p-[22px] flex flex-col gap-[16px]">
        <header className="flex items-center justify-between gap-[10px]">
          {talent.public_code && (
            <span className="text-xs font-semibold tracking-wide bg-purple text-white rounded-full px-[12px] py-[4px]">
              {talent.public_code}
            </span>
          )}
          {sampleCount > 0 && (
            <span className="text-xs text-purple bg-silver rounded-full px-[10px] py-[4px]">
              {samplesCountLabel(sampleCount, lng)}
            </span>
          )}
        </header>

        {groups.meta.length > 0 && (
          <p className="text-gray text-sm">{groups.meta.join(" · ")}</p>
        )}

        {groups.chips.map((field) => (
          <div key={field.key} className="flex flex-col gap-[6px]">
            <span className="text-gray text-xs">{field.label}</span>
            <div className="flex flex-wrap gap-[6px]">
              {field.items.map((item) => (
                <span
                  key={item}
                  className="text-purple text-xs font-medium bg-silver rounded-full px-[10px] py-[4px]"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        ))}

        {groups.rest.map((field) => (
          <div key={field.key} className="flex flex-col">
            <span className="text-gray text-xs">{field.label}</span>
            <span className="text-purple font-medium break-words">{field.text}</span>
          </div>
        ))}

        {groups.prices.length > 0 && (
          <div className="grid grid-cols-2 gap-[8px]">
            {groups.prices.map((price) => (
              <div
                key={price.key}
                className="rounded-xl bg-silver px-[12px] py-[10px] flex flex-col gap-[2px]"
              >
                <span className="text-gray text-[11px] leading-tight">{price.label}</span>
                <span className="text-purple font-bold text-[15px]">{price.amount}</span>
              </div>
            ))}
          </div>
        )}

        {groups.bio.map((field) => (
          <p
            key={field.key}
            className="text-gray text-sm leading-6 line-clamp-3"
            title={field.text}
          >
            {field.text}
          </p>
        ))}

        {sampleCount > 0 ? (
          <div className="mt-auto flex flex-col gap-[10px]">
            {previewTypes.length > 0 && (
              <p className="text-gray text-xs truncate">
                {previewTypes.join(" · ")}
                {sampleCount > previewTypes.length ? " …" : ""}
              </p>
            )}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="w-full flex items-center justify-center gap-[10px] bg-purple text-white rounded-full py-[12px] hover:bg-purpleLight transition-colors"
            >
              <span className="w-[28px] h-[28px] rounded-full bg-white/15 flex items-center justify-center">
                <FaPlay className="w-[10px] h-[10px] ms-[1px]" />
              </span>
              <span className="font-medium">
                {copy.listen}
                <span className="opacity-80"> ({sampleCount})</span>
              </span>
            </button>
          </div>
        ) : (
          <p className="mt-auto text-gray text-sm">{copy.noSamples}</p>
        )}
      </article>

      <SamplesModal
        open={open}
        onClose={() => setOpen(false)}
        talent={talent}
        lng={lng}
      />
    </>
  );
};

export default TalentCard;
